import { isAbortError } from '@app/errors/AbortError'

import { asError } from '@app/packages/asError/asError'

import { sleep } from './sleep'

type Options = {
  /** Wait between attempts, ms. A function receives the error that failed the attempt — e.g. to honour a server's `Retry-After`. */
  interval?: number | ((lastError: Error) => number)
  logErrors?: boolean
  signal?: AbortSignal
  /** Return false for an error no repetition can fix — it is rethrown instead of retried. */
  retryable?: (error: Error) => boolean
} & ({} | { timeout: number } | { attempts: number } | { while: () => boolean })

export class RetryError extends Error {}
export class RetryTimeoutError extends RetryError {}
export class RetryAttemptsLimitError extends RetryError {}

export async function retry<R>(
  fn: () => Promise<R>,
  opts: Options = {
    interval: 1000,
  }
): Promise<R> {
  const now = new Date().getTime()
  let attempt = 0
  // eslint-disable-next-line no-useless-assignment
  let lastError: Error | null = null
  while (true) {
    try {
      return await fn()
    } catch (e) {
      if (isAbortError(e)) throw e
      lastError = asError(e)
      opts.signal?.throwIfAborted()
      if (opts.retryable && !opts.retryable(lastError)) throw e
      console.warn('Retry attempt failed', e)
      if (opts.logErrors) console.error(e)
    }
    ++attempt
    if ('while' in opts) {
      if (!opts.while()) throw new Error('Retry failed with predicate')
    }

    if ('timeout' in opts && new Date().getTime() - now >= opts.timeout) {
      if (lastError) throw lastError
      throw new RetryTimeoutError('Retry failed with timeout')
    }

    if ('attempts' in opts && attempt >= opts.attempts) {
      if (lastError) throw lastError
      throw new RetryAttemptsLimitError('Retry failed with attempts limit exceeding')
    }
    await sleep(typeof opts.interval === 'function' ? opts.interval(lastError) : (opts.interval ?? 1000))
  }
}
